import {IconDeviceGamepad2, IconMaximize, IconTrophy, IconX} from "@tabler/icons-react";
import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import styles from "./MiniGameModal.module.css";

const MiniGameModal = ({isOpen, onClose, onMinimize, games, onGameSelect}) => {
  const {t} = useTranslation();
  const modalRef = useRef(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [activeTab, setActiveTab] = useState("casino");

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = event => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleGameClick = game => {
    setSelectedGame(game);
    // Use onGameSelect instead of navigate for parallel gaming
    onGameSelect(game);
  };

  const handleGameHover = game => {
    setSelectedGame(game);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={styles.miniModalContainer}
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-label={t("quickGamesTitle", "Quick Games")}
    >
      <div className={styles.miniModalHeader}>
        <div className={styles.tabSwitcher}>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === "casino" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("casino")}
            aria-label={t("casino", "Casino")}
            aria-pressed={activeTab === "casino"}
          >
            <IconDeviceGamepad2 size={14} />
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === "sports" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("sports")}
            aria-label={t("sports", "Sports")}
            aria-pressed={activeTab === "sports"}
          >
            <IconTrophy size={14} />
          </button>
        </div>
        <div className={styles.miniModalControls}>
          <button
            type="button"
            className={styles.controlButton}
            onClick={onMinimize}
            aria-label={t("expandQuickGames", "Expand")}
          >
            <IconMaximize size={12} />
          </button>
          <button
            type="button"
            className={styles.controlButton}
            onClick={onClose}
            aria-label={t("hideQuickGames", "Hide")}
          >
            <IconX size={12} />
          </button>
        </div>
      </div>

      <div className={styles.miniModalContent}>
        {activeTab === "casino" && (
          <>
            <div className={styles.gamesList}>
              {games.slice(0, 6).map((game, index) => (
                <button
                  key={game.id || index}
                  type="button"
                  className={`${styles.miniGameItem} ${selectedGame?.id === game.id ? styles.selected : ""}`}
                  onClick={() => handleGameClick(game)}
                  onMouseEnter={() => handleGameHover(game)}
                >
                  <div className={styles.miniGameIcon}>
                    {game.icon ? (
                      <img
                        src={game.icon}
                        alt={game.name || game.game_name}
                        className={styles.gameImage}
                      />
                    ) : (
                      <IconDeviceGamepad2
                        size={14}
                        className={styles.placeholderIcon}
                      />
                    )}
                    <span className={styles.rankBadge}>#{index + 1}</span>
                  </div>
                </button>
              ))}
            </div>

            {selectedGame && (
              <div className={styles.gameInfo}>
                <h4 className={styles.gameName}>{selectedGame.name || selectedGame.game_name}</h4>
                <p className={styles.gameProvider}>{selectedGame.provider}</p>
              </div>
            )}
          </>
        )}

        {activeTab === "sports" && (
          <div className={styles.sportsContent}>
            <p className={styles.sportsMessage}>{t("expandForSports", "Expand for full sports experience")}</p>
            <button
              type="button"
              className={styles.expandButton}
              onClick={onMinimize}
            >
              <IconMaximize size={12} />
              {t("expand", "Expand")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

MiniGameModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onMinimize: PropTypes.func.isRequired,
  games: PropTypes.arrayOf(PropTypes.object).isRequired,
  onGameSelect: PropTypes.func.isRequired,
};

export default MiniGameModal;
